import countryCodes from "components/dashboard/countryISOCodes.json";
import { MercatorCoordinate } from "mapbox-gl";
import { Easing, interpolate } from "remotion";
import { COUNTRY_NAME_ALIASES } from "utils/countryNameAliases";
import { cameraGroundPosition, computeCameraPosition } from "../computeCameraPosition";
import countryShapes from "../geo/countries-110m.json";
import { FOV_TAN_HALF } from "../reelConstants";
import type { WarmupKeyframe } from "../tileWarmup";
import { IntroConfig, IntroStyle, countryRings, overviewCamera } from "./introPlan";
import { interpolateAltitude, interpolateBearing } from "./legHeading";

/**
 * Where the camera is on every frame of a clip intro, as a function of the frame and nothing else.
 *
 * It lives apart from ClipIntro because two things need the same answer. ClipIntro drives the map
 * with it, and the tile warm-up walks it before the render starts. When the warm-up only knew the
 * classic intro's path, a render tab that started in the middle of a clip intro jumped cold to a
 * place with nothing cached: a zoom-out came out black, with the previous frame's tiles as a
 * rectangle in the middle. Deriving the path twice is how those two drift apart.
 *
 * The beats below are the whole timeline of each style, drawing included, so they have one home.
 */

// ---- route-cut (frames at 30 fps) ------------------------------------------------------------------
// The film plays on its own first; then, in order: the flag lands, the country is drawn, the route
// is drawn inside it with a photo flying out of every stop it reaches, a beat to take it in, the
// photos blow away, the country turns into a window onto the map, the window tears open, and the
// camera dives onto the first stop. The last stop is reached at DRAW_OUT and the photos only start
// leaving at CARDS_OUT: that gap is the beat to take it in. Without it the last stops' photos lived
// for ten frames -- out and straight back in.
export const FLAG_SLAP = 14;
export const OUTLINE_IN = 20;
export const OUTLINE_OUT = 48;
export const DRAW_IN = 40;
export const DRAW_OUT = 98;
export const CARDS_OUT = 124;
export const WORDS_OUT = [124, 140];
export const WINDOW_IN = 126;
export const WINDOW_OUT = 140;
export const CUT_IN = 136;
export const CUT_OUT = 166;
export const HOLD_END = 140;
export const GHOST_OUT = [156, 178];

// clip-pin
export const SHRINK_IN = 46;
export const SHRINK_OUT = 82;

// globe-bubble: the window opens, the globe turns to the trip and zooms in, the route is drawn with
// the stops coming out around the window, they fly off, the window swells to the frame, the dive.
export const G_POP = 4;
export const G_SPIN = [4, 66];
export const G_ZOOM = [58, 96];
export const G_DRAW = [84, 114];
export const G_MOONS_OUT = 116;
export const G_WORDS_OUT = [112, 128];
export const G_EXPAND = [118, 146];
export const G_DIVE = 118;
export const G_GHOST = [140, 156];

// card-flip: the film shrinks into a card, hovers, turns over onto the map, the route is drawn on its
// back, and it grows to the frame while the camera dives.
export const F_SHRINK = [12, 40];
export const F_FLIP = [62, 94];
export const F_DRAW = [92, 122];
export const F_WORDS_OUT = [120, 134];
export const F_GROW = [128, 154];
export const F_DIVE = 132;
export const F_GHOST = [150, 166];

// circle-zoom: the map, the circle drawn, the note written, the zoom into the circle with the clip
// opening inside it, the clip on its own, and the clip shrinking into the first stop's pin.
export const CZ_CIRCLE = [8, 32];
export const CZ_NOTE = [28, 44];
export const CZ_ZOOM = [50, 82];
export const CZ_WINDOW = [56, 66];
export const CZ_WORDS_OUT = [128, 142];
export const CZ_SHRINK = [142, 178];

// circle-dive: the circle drawn, the hook written, a beat to read it, the dive through the circle.
// The hook has to be on screen within half a second: that is the whole job of the first second.
export const CD_CIRCLE = [3, 22];
export const CD_NOTE = [10, 28];
export const CD_DIVE = 84;
// ...and the landing: the dive ends above the stop, turned off its heading, and circles it into
// MarkerSequence's first camera, so the landmark has its moment before the photo cards cover it.
export const CD_ORBIT = 150;
export const CD_ORBIT_TURN = 70;
export const CD_ORBIT_RISE = 1.2;

// passport-flip: the passport drops in and opens, one stop stamped per page with the page turned
// after each, then it falls away and the camera dives. The camera holds the whole trip behind it.
export const PF_DROP = [0, 10];
export const PF_OPEN = [11, 23];
/** The window the stamps land in; how many fit is `PASSPORT_PAGES`, the gap between them `PF_EVERY`. */
export const PF_STAMPS = [26, 86];
export const PF_EVERY = [10, 14];
/** A page turns this many frames after its stamp, and takes `PF_TURN[1] - PF_TURN[0]` to go over. */
export const PF_TURN = [5, 9];
export const PASSPORT_PAGES = 7;
export const PF_WORDS_OUT = [92, 104];
export const PF_LEAVE = [94, 108];
export const PF_DIVE = 108;

// passport-photos: a quick cut through the stops' own photos, a stamp slammed on each; then every
// stamp lands together over the blurred map, and the camera dives.
export const PP_MONTAGE = [0, 66];
export const PP_SHOT = [10, 16];
export const PP_SHOTS = 6;
/** Quick after the photos; without any, the stamps are all there is, and land at an easier pace. */
export const PP_EVERY = [3, 12];
export const PP_WORDS_OUT = [92, 104];
export const PP_LEAVE = [94, 108];
export const PP_DIVE = 108;

// boarding-pass: the pass comes up, its plane crosses from code to code, a gloss crosses the pass;
// the code is scanned, BOARDED lands, the stub tears off; the plane lifts off the pass as the route's
// icon tile and flies to the first stop while the pass falls away, and the dive starts where it lands.
export const BP_ENTER = [0, 12];
export const BP_ARC = [10, 40];
export const BP_SHEEN = [14, 32];
export const BP_SCAN = [42, 56];
export const BP_BOARDED = 58;
export const BP_TEAR = [64, 78];
export const BP_LIFT = [76, 86];
export const BP_FLY = [84, 104];
export const BP_PASS_OUT = [80, 98];
export const BP_CLEAR = [90, 106];
export const BP_WORDS_OUT = [84, 96];
export const BP_DIVE = 108;

// ticket-stack: a ticket a leg lands on the pile, at most `TS_TICKETS`; they lay themselves out top to
// bottom, the first is punched, and they drop out of the frame.
export const TS_DROPS = [6, 64];
export const TS_EVERY = [8, 13];
export const TS_TICKETS = 7;
export const TS_SPREAD = [70, 86];
export const TS_PUNCH = 90;
export const TS_LEAVE = [96, 108];
export const TS_WORDS_OUT = [92, 104];
export const TS_DIVE = 108;

// where-to-next: the question flips in, the wheel spins through `WN_GAPS`, slowing, and lands on the
// trip; its nights and departure flip in under it, BOARDING blinks, and the board opens onto the map.
export const WN_HEADER = 2;
export const WN_SPIN = 14;
export const WN_GAPS = [3, 3, 3, 3, 4, 4, 5, 6, 7, 9];
export const WN_LINES = 60;
export const WN_STATUS = 76;
export const WN_OPEN = [86, 106];
export const WN_DIVE = 108;

// departure-board: a stop a row, at most `DB_ROWS`, each flipping in `DB_EVERY` after the last; the
// first row lights up as BOARDING, and the board tips back onto the map.
export const DB_ROWS = 9;
export const DB_FIRST = 8;
export const DB_EVERY = 7;
export const DB_HIGHLIGHT = [72, 84];
export const DB_TIP = [86, 106];
export const DB_DIVE = 108;

// circle-match: the clip, the circle drawn round its subject, the note, the film falling away under a
// circle that stays, the zoom out to the whole trip, the route, and the dive.
export const CM_CIRCLE = [18, 42];
export const CM_NOTE = [36, 52];
export const CM_CUT = [60, 70];
export const CM_OUT = [70, 114];
export const CM_OUTLINE = [86, 112];
export const CM_DRAW = [96, 126];
export const CM_WORDS_OUT = [108, 122];
export const CM_DIVE = 130;
export const CM_GHOST = [150, 166];

export type LngLat = { lng: number; lat: number };

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/** Same lookup the outro's flags use, so a country that resolves there resolves here. */
export const isoOf = (name: string) => {
	const n = COUNTRY_NAME_ALIASES[name] ?? name;
	return (countryCodes as any[]).find((row) => row.country === n)?.alpha2;
};

/** A leg's drawn geometry, thinned: a ferry spline carries thousands of points and the SVG needs ~80. */
export const thin = (coords: number[][], keep = 80) => {
	if (!Array.isArray(coords) || coords.length <= keep) return coords || [];
	const step = Math.ceil(coords.length / keep);
	const out = coords.filter((_, i) => i % step === 0);
	if (out[out.length - 1] !== coords[coords.length - 1]) out.push(coords[coords.length - 1]);
	return out;
};

/** What a leg travels by, as the route draws it: a two-point leg without a vehicle is a flight. */
export const vehicleOf = (stop: any): string | undefined => stop?.vehicle ?? ((stop?.path?.length ?? 0) === 2 ? "plane" : undefined);

/** The styles that frame the trip from above and so draw the country around it. */
const FRAMES_THE_COUNTRY: IntroStyle[] = ["route-cut", "card-flip", "circle-zoom", "circle-match"];

/**
 * The trip in geographic space: its legs, what each leg travels by, every point of it, where each
 * stop is (a leg starts at its stop, the last stop is where the last leg ends), and the outlines of
 * its countries when the style draws them.
 */
export const introGeometry = ({ style, paths, reelData, currentTrip }: { style: IntroStyle; paths: any[]; reelData: any[]; currentTrip: any }) => {
	const legs = (paths || []).map((p: any) => thin(p?.geometry?.coordinates || [])).filter((c) => c.length > 0);
	const legVehicle: (string | undefined)[] = (paths || [])
		.map((p: any, i: number) => ({ p, v: vehicleOf(reelData?.[i]) }))
		.filter(({ p }) => (p?.geometry?.coordinates?.length ?? 0) > 0)
		.map(({ v }) => v);
	const flat = legs.flat();
	const stopsGeo: number[][] = legs.length ? [...legs.map((c) => c[0]), legs[legs.length - 1][legs[legs.length - 1].length - 1]] : [];
	const rings = FRAMES_THE_COUNTRY.includes(style)
		? countryRings((currentTrip?.countries || []).map((c: any) => c?.name).filter(Boolean), flat, countryShapes as any, isoOf)
		: [];
	return { legs, legVehicle, flat, stopsGeo, rings };
};

export type IntroCameraContext = {
	style: IntroStyle;
	config: IntroConfig;
	introFrames: number;
	/** The first stop: where every intro lands. */
	stop: LngLat;
	endAltitude: number;
	endPitch: number;
	endBearing: number;
	width: number;
	height: number;
	scale: number;
	geometry: ReturnType<typeof introGeometry>;
	/** Mapbox's own bounds fit, for the globe window. Without it the globe style guesses. */
	fit?: (bounds: [[number, number], [number, number]], padding: { top: number; bottom: number; left: number; right: number }) => any;
};

/**
 * Where an intro puts things, as fractions of the frame. Portrait is the reel's own shape: the words
 * on top, the route in a band under them, the photos in columns at the frame's edges. A landscape
 * frame has its room sideways instead, so the words take a column on the left and everything the map
 * draws -- route, photos, globe, card -- stands in the stage to the right of them.
 */
export type IntroLayout = {
	landscape: boolean;
	/** The overview camera's band: where the route is framed. */
	band: { top: number; bottom: number; width: number; x: number };
	/** Where the photo columns may stand. */
	cards: { left: number; right: number; top: number; bottom: number };
	/**
	 * Where a thing drawn over the held map stands -- a passport, a boarding pass, a pile of tickets:
	 * under the header with room to breathe (a two-line title and its pills end near 0.31 of a portrait
	 * frame), above Instagram's caption.
	 */
	overlay: { left: number; right: number; top: number; bottom: number };
	/** The middle of the stage, which the globe's window and the card sit on. */
	stage: { x: number; y: number };
	/** How much of the frame's width the words may take. */
	words: number;
};

export const introLayout = (width: number, height: number): IntroLayout =>
	width > height
		? {
				landscape: true,
				band: { top: 0.14, bottom: 0.86, width: 0.3, x: 0.69 },
				cards: { left: 0.4, right: 0.985, top: 0.08, bottom: 0.92 },
				overlay: { left: 0.46, right: 0.985, top: 0.08, bottom: 0.92 },
				stage: { x: 0.69, y: 0.5 },
				words: 0.44,
			}
		: {
				landscape: false,
				band: { top: 0.36, bottom: 0.8, width: 0.74, x: 0.5 },
				cards: { left: 0, right: 1, top: 0.33, bottom: 0.79 },
				overlay: { left: 0, right: 1, top: 0.38, bottom: 0.8 },
				stage: { x: 0.5, y: 0.5 },
				words: 1,
			};

/** `layout.overlay` in pixels: the box a passport, a pass or a pile of tickets stands in. */
export const overlayBox = (layout: IntroLayout, width: number, height: number) => {
	const { left, right, top, bottom } = layout.overlay;
	return { left: left * width, top: top * height, width: (right - left) * width, height: (bottom - top) * height };
};

/**
 * Every number the camera and the drawing both need: the globe window's size, the card's box, and
 * the circle styles' heights and offsets. The drawing sizes the circle by the same altitudes the
 * camera flies, so the circle grows and shrinks with the ground under it.
 */
export const introCameraNumbers = (ctx: IntroCameraContext) => {
	const { style, config, stop, endAltitude, width, height, scale, geometry, fit } = ctx;
	const { flat, rings } = geometry;
	const px = (n: number) => n * scale;
	const layout = introLayout(width, height);
	const stageX = layout.stage.x * width;
	const stageY = layout.stage.y * height;

	// globe-bubble: a plain centre and zoom, turning the globe to the trip and then closing in until
	// the route fits the round window. Mapbox's globe is worldSize / 2pi across at a zoom,
	// worldSize = 512 * 2^zoom, which gives the zoom where it fills the window.
	const bubbleR = px(240);
	const globeState = (f: number) => {
		const lngs = flat.map((c) => c[0]);
		const lats = flat.map((c) => c[1]);
		const bounds: [[number, number], [number, number]] = [
			[Math.min(...lngs), Math.min(...lats)],
			[Math.max(...lngs), Math.max(...lats)],
		];
		const mid = { lng: (bounds[0][0] + bounds[1][0]) / 2, lat: (bounds[0][1] + bounds[1][1]) / 2 };
		const globeZoom = Math.log2((bubbleR * 0.84 * 2 * Math.PI) / 512);
		const side = bubbleR * 2 * 0.62;
		const fitted = fit?.(bounds, { top: stageY - side / 2, bottom: height - stageY - side / 2, left: stageX - side / 2, right: width - stageX - side / 2 });
		const fitZoom = fitted?.zoom ?? globeZoom + 3;
		const fc = fitted?.center as any;
		const fitCentre = fc ? { lng: fc.lng ?? fc[0], lat: fc.lat ?? fc[1] } : mid;
		const spin = interpolate(f, G_SPIN, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const zoomIn = interpolate(f, G_ZOOM, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const turning = { lng: mid.lng - 150 * (1 - spin), lat: 8 + (mid.lat - 8) * spin };
		return {
			center: { lng: turning.lng + (fitCentre.lng - turning.lng) * zoomIn, lat: turning.lat + (fitCentre.lat - turning.lat) * zoomIn },
			zoom: globeZoom + (fitZoom - globeZoom) * zoomIn,
		};
	};

	// card-flip: straight down on the route, framed inside the card rather than the frame.
	const flipW = px(700);
	const flipH = (flipW * 416) / 368;
	const flipCx = stageX;
	const flipCy = layout.landscape ? stageY : px(1075);
	const cardBand = {
		top: (flipCy - flipH / 2 + px(70)) / height,
		bottom: (flipCy + flipH / 2 - px(70)) / height,
		width: (flipW - px(130)) / width,
		x: flipCx / width,
	};

	const framed = rings.length ? [...flat, ...rings.flat()] : flat;
	const overview = style === "card-flip" ? overviewCamera(framed, width / height, cardBand) : overviewCamera(framed, width / height, layout.band);

	// circle styles
	const circleHeld = (overview?.altitude ?? 1) * 0.9;
	// Street level, or the stop's own height if that is lower: close enough that the circle is round a
	// neighbourhood, not a region.
	const circleClose = Math.min(endAltitude, 30000) * 0.8;
	const czAltitude = (f: number) =>
		interpolateAltitude(interpolate(f, CZ_ZOOM, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) }), circleHeld, circleClose);
	const cmAltitude = (f: number) =>
		interpolateAltitude(interpolate(f, CM_OUT, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) }), circleClose, overview?.altitude ?? 1);
	const circleAt = config.circleAt ?? { x: 0.5, y: 0.36 };
	const circleOffset = [(circleAt.x - 0.5) * width, (circleAt.y - 0.5) * height];
	/** Metres per pixel looking straight down from `alt`. */
	const mPerPx = (alt: number) => (2 * alt * FOV_TAN_HALF) / height;
	// circle-dive: straight down from the height at which the circled area is `circleKm` across the
	// circle, with the first stop a little below the middle so the hook has the sky above it.
	const cdRx = px(165);
	const cdAltitude = (((config.circleKm ?? 14) * 1000) / (2 * cdRx)) * (height / (2 * FOV_TAN_HALF));
	const cdAt = config.circleAt ?? { x: 0.5, y: 0.6 };
	const cdOffset = [(cdAt.x - 0.5) * width, (cdAt.y - 0.5) * height];
	/** Where to look so that the first stop lands `off` pixels from the middle of the frame, from `alt`. */
	const targetForStopAt = (off: number[], alt: number): LngLat => ({
		lng: stop.lng - (off[0] * mPerPx(alt)) / (111320 * Math.cos((stop.lat * Math.PI) / 180)),
		lat: stop.lat + (off[1] * mPerPx(alt)) / 111320,
	});
	const overviewOffset = overview
		? [
				((stop.lng - overview.target.lng) * 111320 * Math.cos((stop.lat * Math.PI) / 180)) / mPerPx(overview.altitude),
				(-(stop.lat - overview.target.lat) * 111320) / mPerPx(overview.altitude),
			]
		: [0, 0];

	return {
		layout,
		stageX,
		stageY,
		bubbleR,
		globeState,
		flipW,
		flipH,
		flipCx,
		flipCy,
		overview,
		circleHeld,
		circleClose,
		czAltitude,
		cmAltitude,
		circleAt,
		circleOffset,
		cdRx,
		cdAltitude,
		cdOffset,
		overviewOffset,
		targetForStopAt,
		mPerPx,
	};
};

export type IntroCamera =
	| { kind: "free"; pitch: number; bearing: number; target: LngLat; altitude: number }
	| { kind: "jump"; center: LngLat; zoom: number }
	/** The globe's hand-over: jump to `center`/`zoom`, read the height back, then dive from it at `t`. */
	| { kind: "jump-dive"; center: LngLat; zoom: number; t: number };

/**
 * The dive is a zoom INTO the first stop, not a flight towards it. Scaling the offset between the
 * look-at point and the stop with the altitude keeps the stop on the same pixel while the ground
 * rushes up; the (1 - s) term then walks it to the middle, where MarkerSequence expects it.
 * Interpolating the look-at point straight across instead flew over empty country and found Bangkok
 * in the last few frames.
 */
export const diveCamera = (ctx: IntroCameraContext, from: LngLat, held: number, t: number): IntroCamera => {
	const { stop, endAltitude, endPitch, endBearing } = ctx;
	const s = Easing.inOut(Easing.cubic)(t);
	const a = interpolateAltitude(Easing.inOut(Easing.quad)(t), held, endAltitude);
	const k = (a / held) * (1 - s);
	return {
		kind: "free",
		pitch: endPitch * Easing.inOut(Easing.sin)(t),
		bearing: interpolateBearing(s, 0, endBearing),
		target: { lng: stop.lng - (stop.lng - from.lng) * k, lat: stop.lat - (stop.lat - from.lat) * k },
		altitude: a,
	};
};

/**
 * The styles that hold the whole trip from above -- under a film, a card, a passport, a pass or a board --
 * and the frame on which they dive.
 */
const HOLDS_THE_TRIP: Partial<Record<IntroStyle, number>> = {
	"route-cut": HOLD_END,
	"card-flip": F_DIVE,
	"passport-flip": PF_DIVE,
	"passport-photos": PP_DIVE,
	"boarding-pass": BP_DIVE,
	"ticket-stack": TS_DIVE,
	"where-to-next": WN_DIVE,
	"departure-board": DB_DIVE,
};

/** Where the camera is on `frame`. Null past the intro: from there on the camera is MarkerSequence's. */
export const introCameraAt = (frame: number, ctx: IntroCameraContext, n = introCameraNumbers(ctx)): IntroCamera | null => {
	const { style, introFrames, stop, endAltitude, endPitch, endBearing } = ctx;
	if (frame >= introFrames) return null;
	const free = (pitch: number, bearing: number, target: LngLat, altitude: number): IntroCamera => ({ kind: "free", pitch, bearing, target, altitude });
	/** Settling at the first stop to exactly where MarkerSequence starts, as clip-pin does throughout. */
	const settle = (t: number) =>
		free(
			endPitch,
			interpolateBearing(Easing.out(Easing.cubic)(t), endBearing - 40, endBearing),
			stop,
			interpolateAltitude(Easing.out(Easing.cubic)(t), endAltitude * 3.2, endAltitude)
		);

	const holdEnd = HOLDS_THE_TRIP[style];
	if (holdEnd !== undefined) {
		const ov = n.overview;
		if (!ov) return null;
		// A slow push-in rather than a locked frame: a still map under moving film reads as a screenshot.
		// Everything drawn is projected every frame, so it rides the push exactly.
		const held = ov.altitude * 0.85;
		if (frame < holdEnd) return free(0, 0, ov.target, interpolateAltitude(frame / holdEnd, ov.altitude, held));
		return diveCamera(ctx, ov.target, held, (frame - holdEnd + 1) / (introFrames - holdEnd));
	}
	if (style === "globe-bubble") {
		const g = n.globeState(Math.min(frame, G_DIVE - 1));
		if (frame < G_DIVE) return { kind: "jump", center: g.center, zoom: g.zoom };
		return { kind: "jump-dive", center: g.center, zoom: g.zoom, t: (frame - G_DIVE + 1) / (introFrames - G_DIVE) };
	}
	if (style === "circle-zoom") {
		const ov = n.overview;
		if (!ov) return null;
		if (frame < CZ_ZOOM[0]) return free(0, 0, ov.target, interpolateAltitude(frame / CZ_ZOOM[0], ov.altitude, n.circleHeld));
		if (frame < CZ_ZOOM[1]) {
			// Into the circle. The stop keeps its pixel the whole way, because the circle is drawn round
			// that pixel and grows from it.
			const a = n.czAltitude(frame);
			const k = a / n.circleHeld;
			return free(0, 0, { lng: stop.lng - (stop.lng - ov.target.lng) * k, lat: stop.lat - (stop.lat - ov.target.lat) * k }, a);
		}
		// Behind the clip.
		return settle((frame - CZ_ZOOM[1] + 1) / (introFrames - CZ_ZOOM[1]));
	}
	if (style === "circle-dive") {
		// A slow push while the circle and the hook are drawn, then the dive through the circle: it
		// starts from the look-at point that puts the stop inside the circle, so the stop keeps its pixel
		// while the ground rushes up and the camera tilts into the view MarkerSequence starts from.
		const held = n.cdAltitude * 0.86;
		if (frame < CD_DIVE) {
			const a = interpolateAltitude(frame / CD_DIVE, n.cdAltitude, held);
			return free(0, 0, n.targetForStopAt(n.cdOffset, a), a);
		}
		// A close-up lands low on the landmark and the orbit pulls back to the stop's own height; without
		// one the landing is a little above it and the orbit settles down.
		const close = ctx.config.closeUp === true ? 600 : typeof ctx.config.closeUp === "number" ? ctx.config.closeUp : 0;
		const landAltitude = close > 0 ? Math.min(close, endAltitude) : endAltitude * CD_ORBIT_RISE;
		const landed = { ...ctx, endAltitude: landAltitude, endBearing: endBearing - CD_ORBIT_TURN };
		if (frame < CD_ORBIT) return diveCamera(landed, n.targetForStopAt(n.cdOffset, held), held, (frame - CD_DIVE + 1) / (CD_ORBIT - CD_DIVE));
		const t = Easing.inOut(Easing.sin)((frame - CD_ORBIT + 1) / (introFrames - CD_ORBIT));
		return free(endPitch, interpolateBearing(t, endBearing - CD_ORBIT_TURN, endBearing), stop, interpolateAltitude(t, landAltitude, endAltitude));
	}
	if (style === "circle-match") {
		const ov = n.overview;
		if (!ov) return null;
		// Under the film, already where the cut needs it: the first stop on exactly the pixel the circle
		// is drawn round on the film.
		if (frame < CM_OUT[0]) return free(0, 0, n.targetForStopAt(n.circleOffset, n.circleClose), n.circleClose);
		if (frame < CM_DIVE) {
			const t = interpolate(frame, CM_OUT, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
			const a = n.cmAltitude(frame);
			const off = [n.circleOffset[0] + (n.overviewOffset[0] - n.circleOffset[0]) * t, n.circleOffset[1] + (n.overviewOffset[1] - n.circleOffset[1]) * t];
			return free(0, 0, n.targetForStopAt(off, a), a);
		}
		return diveCamera(ctx, ov.target, ov.altitude, (frame - CM_DIVE + 1) / (introFrames - CM_DIVE));
	}
	// clip-pin: the camera is at the first stop all along, drifting down and round to exactly the height
	// and heading MarkerSequence starts from -- so when the film has shrunk away the map is moving.
	return settle((frame + 1) / introFrames);
};

/** Drive the map to `cam`. */
export const applyIntroCamera = (map: any, cam: IntroCamera | null, ctx: IntroCameraContext) => {
	if (!map || !cam) return;
	const setFree = (c: Extract<IntroCamera, { kind: "free" }>) => {
		const ground = computeCameraPosition(c.pitch, c.bearing, c.target, c.altitude);
		if (!ground) return;
		const camera = map.getFreeCameraOptions();
		camera.setPitchBearing(c.pitch, c.bearing);
		camera.position = MercatorCoordinate.fromLngLat(ground, c.altitude);
		map.setFreeCameraOptions(camera);
	};
	if (cam.kind === "free") return setFree(cam);
	map.jumpTo({ center: [cam.center.lng, cam.center.lat], zoom: cam.zoom, pitch: 0, bearing: 0 });
	if (cam.kind === "jump-dive") {
		// Always set the frame before the dive first, then read back where the free camera stands: that
		// is how the plain camera hands over without a jump.
		const held = map.getFreeCameraOptions().position.toAltitude();
		const dive = diveCamera(ctx, cam.center, held, cam.t);
		if (dive.kind === "free") setFree(dive);
	}
};

/**
 * The height a plain `jumpTo` puts the camera at, worked out rather than read back, for the warm-up
 * that runs before any frame: Mapbox stands the camera half a frame height over tan(fov/2) pixels
 * from the centre, and a pixel at `zoom` is the equator's length times cos(lat) over 512 * 2^zoom.
 */
export const altitudeForZoom = (zoom: number, lat: number, height: number) =>
	((0.5 * height) / FOV_TAN_HALF) * ((40075016.686 * Math.cos((lat * Math.PI) / 180)) / (512 * Math.pow(2, zoom)));

/**
 * The intro's path as warm-up keyframes, every `step` frames. Frame 0 comes first, because it is the
 * thumbnail and, for the styles that open on the map, the first thing anyone sees.
 */
export const introWarmupKeyframes = (ctx: IntroCameraContext, step = 6): WarmupKeyframe[] => {
	const n = introCameraNumbers(ctx);
	const frames: WarmupKeyframe[] = [];
	for (let f = 0; f < ctx.introFrames; f += step) {
		const cam = introCameraAt(f, ctx, n);
		if (!cam) continue;
		if (cam.kind === "jump") {
			frames.push({ lng: cam.center.lng, lat: cam.center.lat, altitude: altitudeForZoom(cam.zoom, cam.center.lat, ctx.height), pitch: 0, bearing: 0 });
			continue;
		}
		const free = cam.kind === "jump-dive" ? diveCamera(ctx, cam.center, altitudeForZoom(cam.zoom, cam.center.lat, ctx.height), cam.t) : cam;
		if (free.kind !== "free") continue;
		const ground = cameraGroundPosition(free.pitch, free.bearing, free.target, free.altitude);
		if (ground && isFinite(ground.lng) && isFinite(ground.lat) && isFinite(free.altitude) && free.altitude > 0) {
			frames.push({ lng: ground.lng, lat: ground.lat, altitude: free.altitude, pitch: free.pitch, bearing: free.bearing });
		}
	}
	return frames;
};
